import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useCrmData } from "./store/store";
import { Sidebar } from "./components/shell/Sidebar";
import { canonicalizeHash, pageFromHash, type Page } from "./lib/route";
import { initAIIntegrationLoader } from "./lib/aiIntegrationLoader";
import { ToastHost } from "./components/shell/Toast";
import { PipelineBoard } from "./components/pipeline/PipelineBoard";
import { CompaniesTable } from "./components/companies/CompaniesTable";
import { CompanyDrawer } from "./components/companies/CompanyDrawer";
import { NewCompanyModal } from "./components/companies/NewCompanyModal";
import { OpportunityDetail } from "./components/opportunity/OpportunityDetail";
import { DashboardPage } from "./components/dashboard/DashboardPage";
import { InvoicesPage } from "./components/billing/InvoicesPage";
import { InvoiceDrawer } from "./components/billing/InvoiceDrawer";
import { InvoiceFormModal } from "./components/billing/InvoiceFormModal";
import AboutPage from "./components/about/AboutPage";
import "./styles.css";

/**
 * Ecranul vine din URL, nu din stare locală: linkurile din sidebar sunt ancore
 * obișnuite, browserul schimbă hash-ul, `hashchange` aduce starea la zi. Așa o
 * navigare programatică (`location.href = "…#/billing"`, cum face scanul
 * configuratorului) se comportă identic cu un click al utilizatorului.
 */
function usePageFromHash(): Page {
  const [page, setPage] = useState<Page>(() => {
    canonicalizeHash();
    return pageFromHash(location.hash);
  });

  useEffect(() => {
    const onHashChange = () => setPage(pageFromHash(location.hash));
    addEventListener("hashchange", onHashChange);
    return () => removeEventListener("hashchange", onHashChange);
  }, []);

  return page;
}

function App() {
  const data = useCrmData();
  const now = new Date();
  const page = usePageFromHash();
  const [openOppId, setOpenOppId] = useState<string | null>(null);
  const [openCompanyId, setOpenCompanyId] = useState<string | null>(null);
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);

  function openOpportunity(id: string) {
    setOpenCompanyId(null);
    setOpenInvoiceId(null);
    setOpenOppId(id);
  }

  function openCompany(id: string) {
    setOpenOppId(null);
    setOpenInvoiceId(null);
    setOpenCompanyId(id);
  }

  function openInvoice(id: string) {
    setOpenOppId(null);
    setOpenCompanyId(null);
    setOpenInvoiceId(id);
  }

  return (
    <div class="app-shell">
      <Sidebar page={page} />
      <main class="app-main">
        {page === "dashboard" && <DashboardPage data={data} now={now} onOpenOpportunity={openOpportunity} onOpenInvoice={openInvoice} />}
        {page === "pipeline" && <PipelineBoard data={data} now={now} onOpenOpportunity={openOpportunity} />}
        {page === "companies" && <CompaniesTable data={data} onOpenCompany={openCompany} onNewCompany={() => setShowNewCompany(true)} />}
        {page === "billing" && <InvoicesPage data={data} now={now} onOpenInvoice={openInvoice} onNewInvoice={() => setShowNewInvoice(true)} />}
        {page === "about" && <AboutPage />}
      </main>

      {openOppId && (
        <OpportunityDetail
          opportunityId={openOppId}
          data={data}
          now={now}
          onClose={() => setOpenOppId(null)}
          onOpenInvoice={openInvoice}
        />
      )}

      {openCompanyId && (
        <CompanyDrawer
          companyId={openCompanyId}
          data={data}
          now={now}
          onClose={() => setOpenCompanyId(null)}
          onOpenOpportunity={openOpportunity}
          onOpenInvoice={openInvoice}
        />
      )}

      {openInvoiceId && (
        <InvoiceDrawer
          invoiceId={openInvoiceId}
          data={data}
          now={now}
          onClose={() => setOpenInvoiceId(null)}
          onOpenCompany={openCompany}
          onOpenOpportunity={openOpportunity}
        />
      )}

      {showNewCompany && (
        <NewCompanyModal
          onClose={() => setShowNewCompany(false)}
          onCreated={(companyId) => {
            setShowNewCompany(false);
            openCompany(companyId);
          }}
        />
      )}

      {showNewInvoice && (
        <InvoiceFormModal
          companies={data.companies}
          opportunities={data.opportunities}
          onClose={() => setShowNewInvoice(false)}
          onCreated={(invoiceId) => {
            setShowNewInvoice(false);
            openInvoice(invoiceId);
          }}
        />
      )}

      <ToastHost />
    </div>
  );
}

render(<App />, document.getElementById("root")!);

// Widgetul concierge al casei — după mount, cheile vin din window.AIG_DEMO
// (servit de /orbit-crm/demo-keys.js), SDK-ul de pe app.<mediu>. Vezi ./lib/aiIntegrationLoader.
initAIIntegrationLoader();
